/**
 * Steering tools the model uses to shape the conversation; their content reaches the chat as
 * profile chips, plan block or result block, never as a step card. The protocol still lists them.
 */
export const HIDDEN_TOOLS = new Set([
	'set_profile',
	'propose_plan',
	'show_results',
	'get_search_capabilities',
	'bought_companies_set',
	'get_operation',
	'cancel_operation',
	'get_results_page',
]);

export const TOOL_NAMES = Object.freeze({
	set_profile: 'Suchprofil',
	get_search_capabilities: 'Suchmöglichkeiten',
	resolve_industry: 'Branche auflösen',
	search_companies: 'Firmen suchen',
	refine_company_set: 'Treffer zusammenführen',
	qualify_candidates: 'KI-Prüfung',
	search_contacts: 'Kontakte zählen',
	probe_maps: 'Google Maps prüfen',
	bought_companies_set: 'Firmen in der Liste',
	propose_plan: 'Plan',
	show_results: 'Ergebnis',
	create_prospect_query: 'Firmen übernehmen',
	start_enrichment: 'Kontakte',
	start_maps_run: 'Google Maps',
	get_operation: 'Hintergrundjob abfragen',
	cancel_operation: 'Hintergrundjob abbrechen',
	get_results_page: 'Ergebnis lesen',
});

const formatCount = (value) => (typeof value === 'number' ? value.toLocaleString('de-DE') : null);

const withTotal = (prefix, summary) => {
	const total = formatCount(summary?.total);
	if (total === null) return prefix;
	const approx = summary.countKind && summary.countKind !== 'exact' ? 'ca. ' : '';
	return `${prefix} · ${approx}${total} Treffer`;
};

const qualifyBreakdown = ({ fit, unfit, unknown }) =>
	[
		`${formatCount(fit) ?? 0} passend`,
		unfit > 0 && `${formatCount(unfit)} ausgeschlossen`,
		unknown > 0 && `${formatCount(unknown)} ohne Website-Daten`,
	]
		.filter(Boolean)
		.join(' · ');

const LABELS = {
	set_profile: () => 'Suchprofil aktualisiert',
	get_search_capabilities: () => 'Suchmöglichkeiten geladen',
	resolve_industry: () => 'Branche aufgelöst',
	search_companies: ({ summary }) => withTotal('Firmen gesucht', summary),
	refine_company_set: ({ summary }) => withTotal('Treffer zusammengeführt', summary),
	qualify_candidates: ({ status, summary }) => {
		if (status === 'pending')
			return `Prüfe ${formatCount(summary?.candidates) ?? 'die'} Firmen gegen die Kriterien …`;
		const checked = formatCount(summary?.checked);
		const text = checked ? `${checked} Firmen geprüft: ${qualifyBreakdown(summary)}` : 'Firmen geprüft';
		return summary?.stale ? `${text} · veraltet` : text;
	},
	search_contacts: () => 'Kontakte im Pool gezählt',
	probe_maps: ({ summary }) =>
		typeof summary?.poolCoverage === 'number'
			? `Maps-Bestand geprüft · ${formatCount(summary.poolCoverage)} im Pool`
			: 'Maps-Bestand geprüft',
	create_prospect_query: ({ summary }) => {
		const created = formatCount(summary?.created) ?? '0';
		const charged = formatCount(summary?.charged) ?? created;
		const existing = summary?.skippedExisting ? ` · ${formatCount(summary.skippedExisting)} bereits vorhanden` : '';
		return `${charged} Credits für ${created} Firmen ausgegeben${existing}`;
	},
	start_enrichment: ({ status, summary }) => {
		if (status === 'pending') {
			return `Kontaktanreicherung gestartet · bis zu ${formatCount(summary?.reserved) ?? '?'} Kontakte reserviert`;
		}
		return 'Kontaktanreicherung abgeschlossen';
	},
	bought_companies_set: ({ summary }) => withTotal('Firmen der Liste als Menge', summary),
	propose_plan: () => 'Plan vorgeschlagen',
	show_results: () => 'Ergebnis zusammengestellt',
	start_maps_run: ({ status, summary }) => {
		if (status === 'pending') return `Externe Suche gestartet · bis zu ${formatCount(summary?.n) ?? '?'} Firmen`;
		return `Externe Suche abgeschlossen · ${formatCount(summary?.created) ?? 0} Firmen übernommen`;
	},
	get_operation: () => 'Hintergrundjob abgefragt',
	cancel_operation: () => 'Hintergrundjob abgebrochen',
	get_results_page: () => 'Firmen der Liste gelesen',
};

/**
 * German one-liner for a tool call in the activity log, built from the counts the event carries.
 *
 * @param {{tool: string, status: 'ok'|'error'|'pending', summary?: object}} action
 * @returns {string}
 */
export function describeAction(action) {
	const label = LABELS[action.tool];
	if (!label) return action.tool;
	return label(action);
}
